/**
 * Pakke-bygger.
 *
 * Plads 1 er produktet, man står på, og ligger i en <template>. De øvrige
 * pladser fyldes fra slideout'en. Ved indsendelse lægges alle varer i kurven
 * i ét kald, hvorefter temaets eget 'cart:update' udsendes, så kurv-tælleren
 * og slideout'en opdaterer sig som ved et normalt køb.
 *
 * Rabatten håndteres IKKE her - den skal være en automatisk Shopify-rabat.
 */
class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.picker = this.querySelector('[data-picker]');
    this.slots = [...this.querySelectorAll('[data-slot]')];
    this.errorEl = this.querySelector('[data-bundle-error]');
    this.submitEl = this.querySelector('[data-bundle-submit]');
    this.submitLabel = this.querySelector('[data-submit-label]');
    this.lockedId = this.querySelector('[data-locked-item]')?.innerHTML.trim();
    this.defaultLabel = this.submitLabel?.textContent ?? '';

    // Gem den tomme plads' markup, så den kan genskabes 1:1 ved fjernelse.
    // Alle tomme pladser er identiske, så én kopi er nok.
    const firstSlot = this.slots[0];
    this.slotTemplate = firstSlot?.innerHTML ?? '';
    const removeButton = firstSlot?.querySelector('[data-remove-slot]');
    this.closeIcon = removeButton?.innerHTML ?? '';
    this.removeLabel = removeButton?.getAttribute('aria-label') ?? '';

    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);

    // Flyt slideout'en ud på body. Produktdetaljerne er position: sticky med
    // z-index paa desktop, hvilket skaber en stacking context - indeni den kan
    // panelet ikke komme over headeren, uanset hvor hoej z-index den faar.
    if (this.picker && this.picker.parentElement !== document.body) {
      document.body.appendChild(this.picker);
      // Klik bobler ikke laengere op til komponenten, saa lyt direkte.
      this.picker.addEventListener('click', this.#onClick);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    // Panelet ligger paa body og ryddes ikke automatisk med komponenten.
    this.picker?.remove();
    document.body.style.overflow = '';
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-open-picker]')) return this.#openPicker();
    if (target.closest('[data-picker-close]')) return this.#closePicker();

    const remove = target.closest('[data-remove-slot]');
    if (remove) return this.#clearSlot(remove.closest('[data-slot]'));

    const pick = target.closest('[data-pick]');
    if (pick) return this.#fillSlot(pick.dataset.variantId, pick.dataset.title);

    if (target.closest('[data-bundle-submit]')) return this.#submit();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape' && !this.picker?.hidden) this.#closePicker();
  };

  #openPicker() {
    if (!this.picker) return;
    this.picker.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  #closePicker() {
    if (!this.picker) return;
    this.picker.hidden = true;
    document.body.style.overflow = '';
  }

  /** Første plads uden valgt vare. */
  #firstEmpty() {
    return this.slots.find((slot) => !slot.querySelector('[data-slot-input]').value);
  }

  #fillSlot(variantId, title) {
    const slot = this.#firstEmpty();
    if (!slot || !variantId) return this.#closePicker();

    slot.innerHTML =
      `<span class="bundle__index"></span>` +
      `<span class="bundle__name"></span>` +
      `<button type="button" class="bundle__remove" data-remove-slot>${this.closeIcon}</button>` +
      `<input type="hidden" data-slot-input>`;

    slot.querySelector('.bundle__index').textContent = String(this.slots.indexOf(slot) + 2);
    slot.querySelector('.bundle__name').textContent = title ?? '';
    slot.querySelector('[data-remove-slot]').setAttribute('aria-label', this.removeLabel);
    slot.querySelector('[data-slot-input]').value = variantId;

    slot.classList.remove('bundle__slot--empty');
    slot.classList.add('bundle__slot--filled');

    this.#setError('');
    this.#closePicker();
  }

  #clearSlot(slot) {
    if (!slot) return;
    slot.innerHTML = this.slotTemplate;
    slot.classList.add('bundle__slot--empty');
    slot.classList.remove('bundle__slot--filled');
  }

  #setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  async #submit() {
    // Mangler der pladser, er det mere hjælpsomt at åbne vælgeren end at fejle.
    if (this.#firstEmpty()) return this.#openPicker();

    const items = [{ id: this.lockedId, quantity: 1 }];
    for (const slot of this.slots) {
      const value = slot.querySelector('[data-slot-input]').value;
      if (value) items.push({ id: value, quantity: 1 });
    }

    this.submitEl?.setAttribute('disabled', '');
    this.#setError('');

    try {
      const url = window.Theme?.routes?.cart_add_url ?? '/cart/add.js';
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.#setError(data.description || data.message || 'Could not add to cart.');
        return;
      }

      const cart = await fetch('/cart.js').then((r) => r.json());

      // Samme eventnavn som temaets egen product-form bruger.
      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: this.id || 'bundle-builder', data: { source: 'bundle-builder', itemCount: cart.item_count } },
        })
      );

      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else {
        window.location.href = this.dataset.cartUrl || '/cart';
      }
    } catch (error) {
      this.#setError('Something went wrong. Please try again.');
    } finally {
      this.submitEl?.removeAttribute('disabled');
      if (this.submitLabel) this.submitLabel.textContent = this.defaultLabel;
    }
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
